import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { EditorableSchema } from 'dashboard/components-lib/FieldSchemaEditor/constants';
import {
  ConfigSection,
  RequireCheckboxContainer,
} from 'dashboard/components-lib/FieldSchemaEditorForm/shared';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FieldSchemaType } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

const TooltipText = styled(Paragraph)`
  max-width: 300px;
`;

interface IdentifierControlsProps {
  fieldSchema: EditorableSchema;
  fieldName: string;
  formContext: ReturnType<typeof useFormContext>;
  canEdit: (fieldSchema: EditorableSchema) => boolean;
}

const IdentifierControls: React.FC<IdentifierControlsProps> = ({
  fieldSchema,
  fieldName,
  formContext,
  canEdit,
}) => {
  const { control, setValue } = formContext;
  const identifierConfigEnabled = useFeatureFlag('dashboard:search-next-frontend-accounts');

  // Ensure that when the field schema key changes, we change the identifier key too
  useEffect(() => {
    if (fieldSchema.identifier != null) {
      setValue(`${fieldName}.identifier.key`, fieldSchema.key);
    }
  }, [fieldName, fieldSchema.identifier, fieldSchema.key, setValue]);

  if (
    !identifierConfigEnabled ||
    fieldSchema.type !== FieldSchemaType.String ||
    // only non-nested fields can be identifiers, base path is fieldSchemas.[index]
    fieldName.split('.').length > 2
  )
    return <></>;

  return (
    <>
      <Spacer height={2} />
      <ConfigSection>
        <Spacer height={2} />
        <Heading size='sm'>Configurations</Heading>
        <Spacer height={2} />
        <RequireCheckboxContainer>
          <Checkbox
            label='Identifier'
            checked={fieldSchema.identifier?.key === fieldSchema.key}
            disabled={!canEdit(fieldSchema)}
            onChange={() => {
              if (fieldSchema.identifier?.key === fieldSchema.key) {
                setValue(`${fieldName}.identifier`, undefined);
              } else {
                setValue(`${fieldName}.identifier`, {
                  key: fieldSchema.key,
                  config: {
                    ['case-sensitive']: false,
                    unique: false,
                  },
                });
              }
            }}
          />
          <Tooltip iconTrigger={<CircleHelpIcon size={14} />}>
            <TooltipText>
              Use this field to store an external ID that can be used to do exact lookups within
              Persona. This setting is currently only applicable to top level string fields.
            </TooltipText>
          </Tooltip>
        </RequireCheckboxContainer>
        {fieldSchema.identifier && (
          <RequireCheckboxContainer>
            <Spacer height={2} />
            <Controller
              name={`${fieldName}.config.case-sensitive`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  label='Case Sensitive'
                  value={value}
                  checked={value}
                  disabled={!canEdit(fieldSchema)}
                  onChange={onChange}
                />
              )}
            />
            <Tooltip iconTrigger={<CircleHelpIcon size={14} />}>
              <TooltipText>
                ‘Case sensitive’ is a field setting that treats &quot;ABC&quot; and &quot;abc&quot;
                as different values for uniqueness.
              </TooltipText>
            </Tooltip>
          </RequireCheckboxContainer>
        )}
        {fieldSchema.identifier && (
          <RequireCheckboxContainer>
            <Controller
              name={`${fieldName}.config.unique`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  label='Unique'
                  value={value}
                  checked={value}
                  disabled={!canEdit(fieldSchema)}
                  onChange={onChange}
                />
              )}
            />
            <Tooltip iconTrigger={<CircleHelpIcon size={14} />}>
              <TooltipText>
                ‘Unique’ is a field setting that prevents the same value from being used in multiple
                records for this field.
              </TooltipText>
            </Tooltip>
          </RequireCheckboxContainer>
        )}
      </ConfigSection>
    </>
  );
};

export default IdentifierControls;
